const BASE_URL = import.meta.env.VITE_API_BASE || 'http://localhost:9000';
let _token = null;

export function setToken(t) { _token = t; }
export function getToken() { return _token; }

export class APIError extends Error {
  constructor(code, message, status, details) {
    super(message || code || 'UNKNOWN');
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// BE wraps response as { data, error } or sometimes flat. We try to be lenient.
function unwrap(body) {
  if (body && typeof body === 'object' && 'data' in body && !('error' in body)) return body.data;
  return body;
}

export async function request(method, path, { body, retries = 2 } = {}) {
  const url = `${BASE_URL}${path}`;
  const headers = { 'Content-Type': 'application/json' };
  if (_token) headers.Authorization = `Bearer ${_token}`;

  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const resp = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined
      });
      const text = await resp.text();
      const parsed = text ? safeJSON(text) : {};

      if (!resp.ok) {
        const err = extractError(parsed, resp.status);
        if (resp.status >= 500 && attempt < retries) {
          await sleep(250 * Math.pow(2, attempt));
          continue;
        }
        throw err;
      }
      return unwrap(parsed);
    } catch (e) {
      lastErr = e;
      if (e instanceof APIError && e.status < 500) throw e;
      if (attempt < retries) await sleep(250 * Math.pow(2, attempt));
    }
  }
  throw lastErr;
}

function safeJSON(t) {
  try { return JSON.parse(t); } catch { return {}; }
}

function extractError(body, status) {
  // ginfx envelope: { code: <int>, message: "...", data?: ... } (flat)
  // or { error: { code, message, details? } } (nested)
  const e = body?.error ?? body ?? {};
  const code = e.code ?? `HTTP_${status}`;
  const message = e.message ?? `HTTP ${status}`;
  let details = e.details ?? null;

  // INSUFFICIENT_STAR — BE returns status 402 with message "INSUFFICIENT_STAR required=3 current=0".
  let normalizedCode = String(code);
  if (status === 402 || (typeof message === 'string' && message.includes('INSUFFICIENT_STAR'))) {
    normalizedCode = 'INSUFFICIENT_STAR';
    if (!details) {
      const m = /required=(\d+)\s+current=(\d+)/.exec(message || '');
      if (m) details = { required: Number(m[1]), current: Number(m[2]) };
    }
  }
  return new APIError(normalizedCode, message, status, details);
}
